/**
 * client/viewmodel/weapmove.ts — BG_CalculateWeaponMovement (pure, no THREE).
 *
 * OWNER: VIEWMODEL+CAMERA agent.
 * Line-by-line port of T5 bgame/bg_weapons_view.cpp (KisakBlack): _Base (stance / locomotion / weapon-state
 * offsets lerped at posMoveRate), _Sway (view-turn sway, DiffTrack), _Recoil (gun kick spring, 5 ms steps),
 * _Bob, BG_SetWeaponMovementAngles/Origin, IdleAngles, DamageKickAngles. T6 keeps the same function set
 * (OpenBO2 bg_weapons_view.cpp) — treat the dvars as ESTIMATE (camera/dvars.ts); every per-weapon number
 * comes from the weapon file (WPN §2.3 "Viewmodel pose offsets", "Hip sway", "Kick").
 *
 * Output space = CoD view-local: x forward, y LEFT, z up (IW AnglesToAxis axis[1] is left, which is what the
 * weapon file's "R" components multiply), angles [pitch, yaw, roll] in degrees relative to the view.
 *
 * Flag mapping (T5 -> ours): eFlags&8 prone -> PMF.PRONE, eFlags&4 crouch -> PMF.DUCKED, pm_flags&8 ladder
 * -> PMF.LADDER, pm_flags&0x400000 dtp -> PMF.DIVE. weaponstate: T5 SPRINT_RAISE/LOOP/DROP 27..29 -> ours
 * 21..23, DTP 40..42 -> 24..26, MELEE_INIT..END 17..19 -> 12..14, RELOADING 10 -> 7.
 */
import { PMF, WeaponState, type PlayerState } from '@shared/types';
import type { WeaponDef } from '@shared/weapons/defs';
import type { Vec3 } from '@shared/math';
import { getBobCycle, weaponBobHorizontal, weaponBobVertical, type BobPs, type WeaponBobDef } from '../camera/bob';
import { BG_WEAPON_BOB_BASE, BG_WEAPON_BOB_LAG, BG_WEAPON_BOB_ROLL, IDLE_FACTOR_RATE } from '../camera/dvars';
import { getLeanFraction } from '../camera/viewmove';

type V3 = [number, number, number];

/** Every weapon-file field the movement code reads (WeaponDef field names in comments). */
export interface WeapMoveDef extends WeaponBobDef {
  aimDownSight: boolean;
  overlayReticle: boolean;
  standMove: V3; standRot: V3; duckedOfs: V3; duckedMove: V3; duckedRot: V3;
  proneOfs: V3; proneMove: V3; proneRot: V3; strafeMove: V3; strafeRot: V3;
  sprintOfs: V3; sprintRot: V3; dtpOfs: V3; dtpRot: V3; mantleOfs: V3; mantleRot: V3;
  posMoveRate: number; posProneMoveRate: number; posRotRate: number; posProneRotRate: number;
  standMoveMinSpeed: number; duckedMoveMinSpeed: number; proneMoveMinSpeed: number;
  adsAimPitch: number;
  sprintInTimeMs: number; sprintOutTimeMs: number; dtpInTimeMs: number; dtpOutTimeMs: number;
  swayMaxAngle: number; swayLerpSpeed: number; swayPitchScale: number; swayYawScale: number; swayHorizScale: number; swayVertScale: number;
  adsSwayMaxAngle: number; adsSwayLerpSpeed: number; adsSwayPitchScale: number; adsSwayYawScale: number; adsSwayHorizScale: number; adsSwayVertScale: number;
  hipIdleAmount: number; hipIdleSpeed: number; adsIdleAmount: number; adsIdleSpeed: number;
  idleCrouchFactor: number; idleProneFactor: number;
  gunMaxPitch: number; gunMaxYaw: number;
  hipGunKickAccel: number; hipGunKickSpeedMax: number; hipGunKickSpeedDecay: number; hipGunKickStaticDecay: number;
  adsGunKickAccel: number; adsGunKickSpeedMax: number; adsGunKickSpeedDecay: number; adsGunKickStaticDecay: number;
  hipGunKickPitchMin: number; hipGunKickPitchMax: number; hipGunKickYawMin: number; hipGunKickYawMax: number;
  adsGunKickPitchMin: number; adsGunKickPitchMax: number; adsGunKickYawMin: number; adsGunKickYawMax: number;
  adsBobFactor: number;
}

export function buildWeapMoveDef(def: WeaponDef): WeapMoveDef {
  const n = (k: string): number => { const v = def.raw[k]; return typeof v === 'number' ? v : 0; };
  const v3 = (p: string, a: string, b: string, c: string): V3 => [n(p + a), n(p + b), n(p + c)];
  const ofs = (p: string): V3 => v3(p, 'F', 'R', 'U');
  const rot = (p: string): V3 => v3(p, 'P', 'Y', 'R');
  const ov = def.raw.overlayReticle ?? def.raw.adsOverlayReticle;
  return {
    aimDownSight: def.aimDownSight,
    overlayReticle: typeof ov === 'string' && ov !== '' && ov.toLowerCase() !== 'none',
    standMove: ofs('standMove'), standRot: rot('standRot'),
    duckedOfs: ofs('duckedOfs'), duckedMove: ofs('duckedMove'), duckedRot: rot('duckedRot'),
    proneOfs: ofs('proneOfs'), proneMove: ofs('proneMove'), proneRot: rot('proneRot'),
    strafeMove: ofs('strafeMove'), strafeRot: rot('strafeRot'),
    sprintOfs: ofs('sprintOfs'), sprintRot: rot('sprintRot'),
    dtpOfs: ofs('dtpOfs'), dtpRot: rot('dtpRot'),
    mantleOfs: ofs('mantleOfs'), mantleRot: rot('mantleRot'),
    posMoveRate: n('posMoveRate'), posProneMoveRate: n('posProneMoveRate'), posRotRate: n('posRotRate'), posProneRotRate: n('posProneRotRate'),
    standMoveMinSpeed: n('standMoveMinSpeed'), duckedMoveMinSpeed: n('duckedMoveMinSpeed'), proneMoveMinSpeed: n('proneMoveMinSpeed'),
    adsAimPitch: n('adsAimPitch'),
    sprintInTimeMs: def.sprintInTimeMs, sprintOutTimeMs: def.sprintOutTimeMs, dtpInTimeMs: def.dtpInTimeMs, dtpOutTimeMs: def.dtpOutTimeMs,
    swayMaxAngle: n('swayMaxAngle'), swayLerpSpeed: n('swayLerpSpeed'), swayPitchScale: n('swayPitchScale'), swayYawScale: n('swayYawScale'),
    swayHorizScale: n('swayHorizScale'), swayVertScale: n('swayVertScale'),
    adsSwayMaxAngle: n('adsSwayMaxAngle'), adsSwayLerpSpeed: n('adsSwayLerpSpeed'), adsSwayPitchScale: n('adsSwayPitchScale'),
    adsSwayYawScale: n('adsSwayYawScale'), adsSwayHorizScale: n('adsSwayHorizScale'), adsSwayVertScale: n('adsSwayVertScale'),
    hipIdleAmount: n('hipIdleAmount'), hipIdleSpeed: n('hipIdleSpeed'), adsIdleAmount: n('adsIdleAmount'), adsIdleSpeed: n('adsIdleSpeed'),
    idleCrouchFactor: n('idleCrouchFactor'), idleProneFactor: n('idleProneFactor'),
    gunMaxPitch: n('gunMaxPitch'), gunMaxYaw: n('gunMaxYaw'),
    hipGunKickAccel: n('hipGunKickAccel'), hipGunKickSpeedMax: n('hipGunKickSpeedMax'), hipGunKickSpeedDecay: n('hipGunKickSpeedDecay'), hipGunKickStaticDecay: n('hipGunKickStaticDecay'),
    adsGunKickAccel: n('adsGunKickAccel'), adsGunKickSpeedMax: n('adsGunKickSpeedMax'), adsGunKickSpeedDecay: n('adsGunKickSpeedDecay'), adsGunKickStaticDecay: n('adsGunKickStaticDecay'),
    hipGunKickPitchMin: n('hipGunKickPitchMin'), hipGunKickPitchMax: n('hipGunKickPitchMax'), hipGunKickYawMin: n('hipGunKickYawMin'), hipGunKickYawMax: n('hipGunKickYawMax'),
    adsGunKickPitchMin: n('adsGunKickPitchMin'), adsGunKickPitchMax: n('adsGunKickPitchMax'), adsGunKickYawMin: n('adsGunKickYawMin'), adsGunKickYawMax: n('adsGunKickYawMax'),
    adsBobFactor: n('adsBobFactor'),
    sprintBob: [n('sprintBobH'), n('sprintBobV')],
    dtpBob: [n('dtpBobH'), n('dtpBobV')],
  };
}

/** The PlayerState fields used (ps = predicted state, viewangles at render rate). */
export type WeapMovePs = BobPs & Pick<PlayerState, 'weaponState' | 'weaponTime' | 'speed' | 'velocity' | 'leanf'> & { viewangles: Readonly<Vec3> };

/** universal/com_math.cpp DiffTrack. */
export function diffTrack(tgt: number, cur: number, rate: number, dt: number): number {
  const err = tgt - cur;
  const step = rate * err * dt;
  if (Math.abs(err) <= 0.001) return tgt;
  if (Math.abs(step) > Math.abs(err)) return tgt;
  return cur + step;
}

/** DiffTrackAngle (result wrapped to [-180, 180)). */
export function diffTrackAngle(tgt: number, cur: number, rate: number, dt: number): number {
  while (tgt - cur > 180) tgt -= 360;
  while (tgt - cur < -180) tgt += 360;
  const a = diffTrack(tgt, cur, rate, dt) / 360;
  return (a - Math.floor(a + 0.5)) * 360;
}

function angleNorm180(a: number): number {
  a = ((a % 360) + 360) % 360;
  return a > 180 ? a - 360 : a;
}

/** BG_CalculateWeaponMovement_GunRecoil_SingleAngle. Returns true when the axis has come to rest. */
export function gunRecoilSingleAngle(ofs: number[], spd: number[], i: number, dt: number, cap: number, accel: number, speedMax: number, speedDecay: number, staticDecay: number): boolean {
  if (Math.abs(ofs[i]) < 0.25 && Math.abs(spd[i]) < 1) { ofs[i] = 0; spd[i] = 0; return true; }
  ofs[i] += spd[i] * dt;
  if (ofs[i] > cap) { ofs[i] = cap; if (spd[i] > 0) spd[i] = 0; } else if (-cap > ofs[i]) { ofs[i] = -cap; if (spd[i] < 0) spd[i] = 0; }
  if (ofs[i] > 0) spd[i] -= accel * dt; else if (ofs[i] < 0) spd[i] += accel * dt;
  spd[i] -= spd[i] * speedDecay * dt;
  if (spd[i] <= 0) { spd[i] += staticDecay * dt; if (spd[i] > 0) spd[i] = 0; } else { spd[i] -= staticDecay * dt; if (spd[i] < 0) spd[i] = 0; }
  if (spd[i] > speedMax) spd[i] = speedMax; else if (-speedMax > spd[i]) spd[i] = -speedMax;
  return false;
}

export interface WeapMoveFrame {
  ps: WeapMovePs;
  def: WeapMoveDef;
  /** cg.xyspeed. */
  xyspeed: number;
  /** Seconds since last frame. */
  frametime: number;
  /** Mantle fraction 0..1 when mantling (Mantle_GetMantleFrac), else -1. */
  mantleFrac: number;
}

/** weaponState_t + the cg fields it is saved back into (CG_BuildWeaponState / CG_SaveWeaponState). */
export class WeaponMovement {
  readonly baseOrigin: Vec3 = [0, 0, 0];
  readonly baseAngles: Vec3 = [0, 0, 0];
  readonly swayOrigin: Vec3 = [0, 0, 0];
  readonly swayAngles: Vec3 = [0, 0, 0];
  readonly swayViewAngles: Vec3 = [0, 0, 0];
  readonly recoilAngles: number[] = [0, 0, 0];
  readonly recoilSpeed: number[] = [0, 0, 0];
  readonly bobAngles: Vec3 = [0, 0, 0];
  lastIdleFactor = 1;
  weapIdleTime = 0;
  /** cg.resetWeaponStateFlag: first frame after a spawn / weapon change uses frametime 1 and snaps sway. */
  resetFlag = true;

  reset(): void {
    for (const v of [this.baseOrigin, this.baseAngles, this.swayOrigin, this.swayAngles, this.bobAngles]) { v[0] = v[1] = v[2] = 0; }
    this.recoilAngles.fill(0); this.recoilSpeed.fill(0);
    this.resetFlag = true;
  }

  /** BG_WeaponFireRecoil (gun part): add to the recoil spring speed. r1/r2 in [0,1). */
  fire(def: WeapMoveDef, adsFrac: number, r1: number, r2: number): void {
    let p: number, y: number;
    if (adsFrac <= 0) {
      p = r1 * (def.hipGunKickPitchMax - def.hipGunKickPitchMin) + def.hipGunKickPitchMin;
      y = r2 * (def.hipGunKickYawMax - def.hipGunKickYawMin) + def.hipGunKickYawMin;
    } else {
      p = r1 * (def.adsGunKickPitchMax - def.adsGunKickPitchMin) + def.adsGunKickPitchMin;
      y = r2 * (def.adsGunKickYawMax - def.adsGunKickYawMin) + def.adsGunKickYawMin;
    }
    this.recoilSpeed[0] += p;
    this.recoilSpeed[1] += y;
  }

  /** BG_CalculateWeaponMovement: run the state for one frame; outputs view-local origin / angles. */
  calculate(f: WeapMoveFrame, origin: Vec3, angles: Vec3, idleExtra?: (angles: Vec3) => void): void {
    if (this.resetFlag) {
      f = { ...f, frametime: 1 };
      this.swayViewAngles[0] = f.ps.viewangles[0]; this.swayViewAngles[1] = f.ps.viewangles[1]; this.swayViewAngles[2] = f.ps.viewangles[2];
      this.resetFlag = false;
    }
    this.base(f);
    this.sway(f);
    this.recoil(f);
    this.bob(f);
    this.setAngles(f, angles, idleExtra);
    this.setOrigin(f, origin);
  }

  // ---- _Base -----------------------------------------------------------------------------------
  private base(f: WeapMoveFrame): void {
    const { ps, def } = f;
    const prone = (ps.pm_flags & PMF.PRONE) !== 0;
    const crouched = !prone && (ps.pm_flags & PMF.DUCKED) !== 0;
    const tp: V3 = [0, 0, 0], ta: V3 = [0, 0, 0];
    let minSpeed = prone ? def.proneMoveMinSpeed : crouched ? def.duckedMoveMinSpeed : def.standMoveMinSpeed;
    if (minSpeed < 0) minSpeed = 0;
    const moving = f.xyspeed > minSpeed && ps.speed > minSpeed;
    const reloading = ps.weaponState === WeaponState.RELOADING;
    const ladder = (ps.pm_flags & PMF.LADDER) !== 0;
    if (prone || crouched) this.stance(f, prone, crouched, tp, ta);
    if (moving && !ladder && !reloading) this.movement(f, prone, crouched, minSpeed, tp, ta);
    this.weaponStateOfs(f, tp, ta);
    // _Base_Internal_Save
    let oRate: number, rRate: number;
    if (prone) { oRate = f.frametime * def.posProneMoveRate; rRate = f.frametime * def.posProneRotRate; }
    else { oRate = f.frametime * def.posMoveRate; rRate = f.frametime * def.posRotRate; }
    if (1 - oRate < 0) oRate = 1;
    if (1 - rRate < 0) rRate = 1;
    for (let i = 0; i < 3; i++) {
      this.baseOrigin[i] += oRate * (tp[i] - this.baseOrigin[i]);
      this.baseAngles[i] += rRate * (ta[i] - this.baseAngles[i]);
    }
  }

  private stance(f: WeapMoveFrame, prone: boolean, crouched: boolean, tp: V3, ta: V3): void {
    const { ps, def } = f;
    const melee = ps.weaponState === WeaponState.MELEE_INIT || ps.weaponState === WeaponState.MELEE_FIRE || ps.weaponState === WeaponState.MELEE_END;
    if (crouched && !melee) { tp[0] += def.duckedOfs[0]; tp[1] += def.duckedOfs[1]; tp[2] += def.duckedOfs[2]; }
    else if (prone && !melee) { tp[0] += def.proneOfs[0]; tp[1] += def.proneOfs[1]; tp[2] += def.proneOfs[2]; }
    if (def.aimDownSight) ta[0] += ps.adsFrac * def.adsAimPitch;
  }

  private movement(f: WeapMoveFrame, prone: boolean, crouched: boolean, minSpeed: number, tp: V3, ta: V3): void {
    const { ps, def } = f;
    const isProne = prone && (ps.pm_flags & PMF.DIVE) === 0;
    const move = isProne ? def.proneMove : crouched ? def.duckedMove : def.standMove;
    const rot = isProne ? def.proneRot : crouched ? def.duckedRot : def.standRot;
    const denom = ps.speed - minSpeed;
    let speedFrac = denom > 0 ? (f.xyspeed - minSpeed) / denom : 1;
    if (speedFrac - 1 >= 0) speedFrac = 1;
    if (speedFrac < 0) speedFrac = 0;
    let angleWeight = 1 - ps.adsFrac;
    if (angleWeight < 0) angleWeight = 0; else if (angleWeight > 1) angleWeight = 1;
    const w = speedFrac * angleWeight;
    for (let i = 0; i < 3; i++) { tp[i] += speedFrac * move[i]; ta[i] += w * rot[i]; }
    // strafe: BG_GetPlayerViewDirection right vector (yaw only matters for the xy dot)
    const yaw = (ps.viewangles[1] * Math.PI) / 180;
    const rx = Math.sin(yaw), ry = -Math.cos(yaw); // right = (sin y, -cos y) for pitch/roll 0
    if (f.xyspeed <= 0) return;
    let strafe = ((ps.velocity[0] * rx + ps.velocity[1] * ry) * speedFrac) / f.xyspeed;
    if (strafe > 1.01) strafe = 1.01; else if (strafe < -1.01) strafe = -1.01;
    const sw = strafe * angleWeight;
    for (let i = 0; i < 3; i++) { tp[i] += strafe * def.strafeMove[i]; ta[i] += sw * def.strafeRot[i]; }
  }

  private weaponStateOfs(f: WeapMoveFrame, tp: V3, ta: V3): void {
    const { ps, def } = f;
    const s = ps.weaponState;
    let lerp: number, mv: V3, rt: V3;
    if (s >= WeaponState.SPRINT_RAISE && s <= WeaponState.SPRINT_DROP) {
      if (s === WeaponState.SPRINT_DROP && def.sprintOutTimeMs) lerp = ps.weaponTime / def.sprintOutTimeMs;
      else if (s === WeaponState.SPRINT_RAISE && def.sprintInTimeMs) lerp = 1 - ps.weaponTime / def.sprintInTimeMs;
      else lerp = 1;
      mv = def.sprintOfs; rt = def.sprintRot;
    } else if (s >= WeaponState.DTP_IN && s <= WeaponState.DTP_OUT) {
      if (s === WeaponState.DTP_OUT && def.dtpOutTimeMs) lerp = ps.weaponTime / def.dtpOutTimeMs;
      else if (s === WeaponState.DTP_IN && def.dtpInTimeMs) lerp = 1 - ps.weaponTime / def.dtpInTimeMs;
      else lerp = 1;
      mv = def.dtpOfs; rt = def.dtpRot;
    } else {
      if (f.mantleFrac < 0) return;
      lerp = f.mantleFrac; mv = def.mantleOfs; rt = def.mantleRot;
    }
    if (lerp > 0) for (let i = 0; i < 3; i++) { tp[i] += lerp * mv[i]; ta[i] += lerp * rt[i]; }
  }

  // ---- _Sway -----------------------------------------------------------------------------------
  private sway(f: WeapMoveFrame): void {
    if (f.frametime === 0) return;
    const { ps, def } = f;
    const a = ps.adsFrac;
    let maxA: number, lerpS: number, pS: number, yS: number, hS: number, vS: number;
    if (def.aimDownSight) {
      if (a > 0 && def.overlayReticle) { this.copyView(ps); return; }
      maxA = (1 - a) * def.swayMaxAngle + def.adsSwayMaxAngle * a;
      lerpS = (1 - a) * def.swayLerpSpeed + def.adsSwayLerpSpeed * a;
      pS = (1 - a) * def.swayPitchScale + def.adsSwayPitchScale * a;
      yS = (1 - a) * def.swayYawScale + def.adsSwayYawScale * a;
      hS = (1 - a) * def.swayHorizScale + def.adsSwayHorizScale * a;
      vS = (1 - a) * def.swayVertScale + def.adsSwayVertScale * a;
    } else {
      maxA = def.swayMaxAngle; lerpS = def.swayLerpSpeed; pS = def.swayPitchScale; yS = def.swayYawScale;
      hS = def.swayHorizScale; vS = def.swayVertScale;
    }
    // shell shock scale: no shellshock in SnS -> 1
    const k = 1 / (60 * f.frametime);
    let d0 = angleNorm180(ps.viewangles[0] - this.swayViewAngles[0]) * k;
    let d1 = angleNorm180(ps.viewangles[1] - this.swayViewAngles[1]) * k;
    d0 = d0 - maxA < 0 ? d0 : maxA; if (-maxA - d0 >= 0) d0 = -maxA;
    d1 = d1 - maxA < 0 ? d1 : maxA; if (-maxA - d1 >= 0) d1 = -maxA;
    this.swayOrigin[1] = diffTrack(d1 * hS, this.swayOrigin[1], lerpS, f.frametime);
    this.swayOrigin[2] = diffTrack(d0 * vS, this.swayOrigin[2], lerpS, f.frametime);
    this.swayAngles[0] = diffTrackAngle(d0 * pS, this.swayAngles[0], lerpS, f.frametime);
    this.swayAngles[1] = diffTrackAngle(d1 * yS, this.swayAngles[1], lerpS, f.frametime);
    this.copyView(ps);
  }

  private copyView(ps: WeapMovePs): void {
    this.swayViewAngles[0] = ps.viewangles[0]; this.swayViewAngles[1] = ps.viewangles[1]; this.swayViewAngles[2] = ps.viewangles[2];
  }

  // ---- _Recoil ---------------------------------------------------------------------------------
  private recoil(f: WeapMoveFrame): void {
    const { def } = f;
    if (!def.aimDownSight) return; // T5: only for BG_IsAimDownSightWeapon
    const p = f.ps.adsFrac;
    const accel = (def.adsGunKickAccel - def.hipGunKickAccel) * p + def.hipGunKickAccel;
    const smax = (def.adsGunKickSpeedMax - def.hipGunKickSpeedMax) * p + def.hipGunKickSpeedMax;
    const sdec = (def.adsGunKickSpeedDecay - def.hipGunKickSpeedDecay) * p + def.hipGunKickSpeedDecay;
    const stdec = (def.adsGunKickStaticDecay - def.hipGunKickStaticDecay) * p + def.hipGunKickStaticDecay;
    let total = f.frametime;
    while (total > 0) {
      let step: number;
      if (total <= 0.0049999999) { step = total; total = 0; } else { step = 0.0049999999; total -= 0.0049999999; }
      const stop0 = gunRecoilSingleAngle(this.recoilAngles, this.recoilSpeed, 0, step, def.gunMaxPitch, accel, smax, sdec, stdec);
      const stop1 = gunRecoilSingleAngle(this.recoilAngles, this.recoilSpeed, 1, step, def.gunMaxYaw, accel, smax, sdec, stdec);
      if (stop1 && stop0) break;
    }
  }

  // ---- _Bob ------------------------------------------------------------------------------------
  private bob(f: WeapMoveFrame): void {
    const { ps, def } = f;
    const cycle = Math.PI * BG_WEAPON_BOB_LAG + getBobCycle(ps.bobCycle) + 2 * Math.PI;
    const speed = f.xyspeed * BG_WEAPON_BOB_BASE;
    this.bobAngles[0] = -weaponBobVertical(ps, def, cycle, speed);
    this.bobAngles[1] = -weaponBobHorizontal(ps, def, cycle, speed);
    const r = weaponBobHorizontal(ps, def, cycle - 0.47123894, speed * BG_WEAPON_BOB_ROLL);
    this.bobAngles[2] = 0 - r < 0 ? 0 : r;
    if (ps.adsFrac > 0) {
      const k = 1 - (1 - def.adsBobFactor) * ps.adsFrac;
      this.bobAngles[0] *= k; this.bobAngles[1] *= k; this.bobAngles[2] *= k;
    }
  }

  // ---- BG_SetWeaponMovementAngles / Origin -------------------------------------------------------
  private setAngles(f: WeapMoveFrame, angles: Vec3, idleExtra?: (angles: Vec3) => void): void {
    const { ps, def } = f;
    angles[0] = angles[1] = angles[2] = 0;
    const adsW = 1 - ps.adsFrac * 2; // BaseAngles: base offsets fade out over the first half of ADS
    if (adsW > 0) { angles[0] += adsW * this.baseAngles[0]; angles[1] += adsW * this.baseAngles[1]; angles[2] += adsW * this.baseAngles[2]; }
    angles[0] = angleNorm180(angles[0] - this.swayAngles[0]);
    angles[1] = angleNorm180(angles[1] - this.swayAngles[1]);
    if (ps.leanf !== 0) angles[2] -= getLeanFraction(ps.leanf) * 2;
    angles[0] += this.recoilAngles[0]; angles[1] += this.recoilAngles[1]; angles[2] += this.recoilAngles[2];
    if (def.overlayReticle) {
      const w = 1 - ps.adsFrac;
      angles[0] += w * this.bobAngles[0]; angles[1] += w * this.bobAngles[1]; angles[2] += w * this.bobAngles[2];
    } else {
      angles[0] += this.bobAngles[0]; angles[1] += this.bobAngles[1]; angles[2] += this.bobAngles[2];
    }
    this.idleAngles(f, angles);
    if (idleExtra) idleExtra(angles); // damage kick angles (need the camera's clock)
  }

  /** BG_ComputeAndApplyWeaponMovement_IdleAngles. */
  private idleAngles(f: WeapMoveFrame, angles: Vec3): void {
    const { ps, def } = f;
    let scale: number, speed: number;
    if (def.aimDownSight) {
      scale = (def.adsIdleAmount - def.hipIdleAmount) * ps.adsFrac + def.hipIdleAmount;
      speed = (def.adsIdleSpeed - def.hipIdleSpeed) * ps.adsFrac + def.hipIdleSpeed;
    } else {
      scale = def.hipIdleAmount; speed = def.hipIdleSpeed;
    }
    const prone = (ps.pm_flags & PMF.PRONE) !== 0;
    const crouched = !prone && (ps.pm_flags & PMF.DUCKED) !== 0;
    const target = prone ? def.idleProneFactor : crouched ? def.idleCrouchFactor : 1;
    const fs = f.frametime * IDLE_FACTOR_RATE;
    if (target > this.lastIdleFactor) this.lastIdleFactor = Math.min(this.lastIdleFactor + fs, target);
    else this.lastIdleFactor = Math.max(this.lastIdleFactor - fs, target);
    scale *= this.lastIdleFactor;
    if (def.overlayReticle) scale *= 1 - ps.adsFrac;
    this.weapIdleTime += Math.trunc(f.frametime * 1000 * speed);
    const t = this.weapIdleTime, s = scale * 0.01;
    angles[2] += s * Math.sin(t * 0.0005);
    angles[1] += s * Math.sin(t * 0.0007);
    angles[0] += s * Math.sin(t * 0.001);
  }

  private setOrigin(f: WeapMoveFrame, origin: Vec3): void {
    const { ps } = f;
    origin[0] = origin[1] = origin[2] = 0;
    const adsW = 1 - ps.adsFrac * 2;
    if (adsW > 0) { origin[0] += adsW * this.baseOrigin[0]; origin[1] += adsW * this.baseOrigin[1]; origin[2] += adsW * this.baseOrigin[2]; }
    origin[1] -= this.swayOrigin[1];
    origin[2] += this.swayOrigin[2];
    if (ps.leanf !== 0 && ps.adsFrac < 1) {
      const lf = getLeanFraction(ps.leanf);
      const roll = (-lf * 2 * Math.PI) / 180;
      const dist = (1 - ps.adsFrac) * lf * 1.6;
      // AngleVectors right for (0,0,roll) = (0, -cos r, -sin r) in (fwd, left, up)
      origin[1] += dist * -Math.cos(roll); origin[2] += dist * -Math.sin(roll);
    }
    // BG_ApplyWeaponMovement_BobOrigin: rotate the origin by the bob angles
    rotateByAngles(origin, this.bobAngles);
  }
}

/** origin = origin * AnglesToAxis(angles) (row vector, like MatrixTransformVector). Space: fwd, left, up. */
export function rotateByAngles(v: Vec3, angles: Readonly<Vec3>): void {
  const ax = anglesToAxis(angles);
  const x = v[0], y = v[1], z = v[2];
  v[0] = x * ax[0] + y * ax[3] + z * ax[6];
  v[1] = x * ax[1] + y * ax[4] + z * ax[7];
  v[2] = x * ax[2] + y * ax[5] + z * ax[8];
}

/** IW AnglesToAxis -> [fwd(3), left(3), up(3)] (Float64 rows). */
export function anglesToAxis(angles: Readonly<Vec3>): number[] {
  const d = Math.PI / 180;
  const sy = Math.sin(angles[1] * d), cy = Math.cos(angles[1] * d);
  const sp = Math.sin(angles[0] * d), cp = Math.cos(angles[0] * d);
  const sr = Math.sin(angles[2] * d), cr = Math.cos(angles[2] * d);
  const fwd = [cp * cy, cp * sy, -sp];
  const right = [-sr * sp * cy + cr * sy, -sr * sp * sy - cr * cy, -sr * cp];
  const up = [cr * sp * cy + sr * sy, cr * sp * sy - sr * cy, cr * cp];
  return [fwd[0], fwd[1], fwd[2], -right[0], -right[1], -right[2], up[0], up[1], up[2]];
}
